import { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, updateItem, deleteItem } from './fitnessApi';
import { parseTemplateSets } from './liftingBurn';
import { useUserProfile, getNicknameForEmail } from '../UserProfileContext';

const LIST = 'LiftingTemplates';

// Sets JSON is a bare array of exercise prescriptions:
//   { exercise, sets, reps, weightLb, restSeconds, notes,
//     muscleGroups: ['quads', ...],           — feeds the weekly hard-set card
//     alt: { exercise, reps, weightLb, ... }  — per-exercise dumbbell fallback
//   }
// Unknown fields on a row are preserved through an edit — the editor
// spreads, never rebuilds from its own field list. weightLb saves as null
// when blank: "not chosen yet", never 0 lb. Filling it in here is the
// durable prefill for the runner. The retired { variants: {...} } shape
// parses by flattening to its first variant, one-way — saving rewrites it
// as the bare array.

function emptyRow() {
  return { exercise: '', sets: 3, reps: 8, weightLb: null, restSeconds: 90, notes: '', muscleGroups: [] };
}

function serializeSets(rows) {
  return JSON.stringify(rows.map(r => {
    const cleaned = {
      ...r,
      exercise: (r.exercise || '').trim(),
      sets: Number(r.sets) || 1,
      reps: Number(r.reps) || 1,
      weightLb: r.weightLb === '' || r.weightLb === null || r.weightLb === undefined ? null : Number(r.weightLb),
      restSeconds: Number(r.restSeconds) || 0,
      muscleGroups: Array.isArray(r.muscleGroups) ? r.muscleGroups.filter(Boolean) : [],
    };
    // A blank alt exercise means no fallback — drop the key rather than
    // save an alt that swaps to nothing.
    if (cleaned.alt && !(cleaned.alt.exercise || '').trim()) delete cleaned.alt;
    else if (cleaned.alt) {
      cleaned.alt = {
        ...cleaned.alt,
        exercise: cleaned.alt.exercise.trim(),
        reps: cleaned.alt.reps === '' || cleaned.alt.reps === null || cleaned.alt.reps === undefined ? null : Number(cleaned.alt.reps),
        weightLb: cleaned.alt.weightLb === '' || cleaned.alt.weightLb === null || cleaned.alt.weightLb === undefined ? null : Number(cleaned.alt.weightLb),
      };
    }
    return cleaned;
  }));
}

// 'quads, glutes' <-> ['quads','glutes'] for the editor's one text input.
function musclesToText(arr) {
  return Array.isArray(arr) ? arr.join(', ') : '';
}

function textToMuscles(text) {
  return text.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
}

function LiftingTemplates({ getToken, userEmail }) {
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // { id?, name, description, variantNames, byVariant }
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('mine');
  const [nicknames, setNicknames] = useState({});
  const [copying, setCopying] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Same context read the swim tab makes; the value is unused here too, but
  // the hook keeps the provider warm for getNicknameForEmail.
  useUserProfile();

  const fetchTemplates = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const items = await getItems(token, LIST);
      const mapped = items.map(it => ({
        id: it.id,
        name: it.Title || '',
        description: it.Description || '',
        rows: parseTemplateSets(it.Sets),
        createdByEmail: it.CreatedByEmail || '',
      }));
      setAllTemplates(mapped);

      const emails = [...new Set(mapped.map(t => t.createdByEmail).filter(Boolean))];
      const resolved = {};
      for (const email of emails) {
        resolved[email] = await getNicknameForEmail(token, email);
      }
      setNicknames(resolved);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const myTemplates = allTemplates.filter(
    t => t.createdByEmail.toLowerCase() === userEmail.toLowerCase()
  );
  const familyTemplates = allTemplates.filter(
    t => t.createdByEmail.toLowerCase() !== userEmail.toLowerCase()
  );
  const displayedTemplates = viewMode === 'mine' ? myTemplates : familyTemplates;

  function startNew() {
    setEditing({ name: '', description: '', rows: [emptyRow()] });
  }

  function startEdit(tmpl) {
    // Deep copy so Cancel is free.
    setEditing(JSON.parse(JSON.stringify({
      id: tmpl.id,
      name: tmpl.name,
      description: tmpl.description,
      rows: tmpl.rows,
    })));
  }

  async function handleCopy(tmpl) {
    setCopying(tmpl.id);
    setError(null);
    try {
      const token = await getToken();
      await createItem(token, LIST, {
        Title: `${tmpl.name} (copy)`,
        Description: tmpl.description,
        Sets: serializeSets(tmpl.rows),
        CreatedByEmail: userEmail,
      });
      await fetchTemplates();
      setViewMode('mine');
    } catch (err) {
      setError(err.message);
    } finally {
      setCopying(null);
    }
  }

  async function handleDelete(tmpl) {
    if (!window.confirm(`Delete template "${tmpl.name}"?`)) return;
    try {
      const token = await getToken();
      await deleteItem(token, LIST, tmpl.id);
      setAllTemplates(prev => prev.filter(t => t.id !== tmpl.id));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleSave() {
    if (!editing.name.trim()) { setError('Template name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const fields = {
        Title: editing.name.trim(),
        Description: editing.description,
        Sets: serializeSets(editing.rows),
        CreatedByEmail: userEmail,
      };
      if (editing.id) {
        await updateItem(token, LIST, editing.id, fields);
      } else {
        await createItem(token, LIST, fields);
      }
      setEditing(null);
      await fetchTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function updateRow(index, field, value) {
    setEditing(prev => {
      const rows = [...prev.rows];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, rows };
    });
  }

  // The alt sub-object edits through the same door; a row without one gets
  // it created on first keystroke.
  function updateAlt(index, field, value) {
    setEditing(prev => {
      const rows = [...prev.rows];
      rows[index] = { ...rows[index], alt: { ...(rows[index].alt || {}), [field]: value } };
      return { ...prev, rows };
    });
  }

  function addRow() {
    setEditing(prev => ({ ...prev, rows: [...prev.rows, emptyRow()] }));
  }

  function removeRow(index) {
    setEditing(prev => ({ ...prev, rows: prev.rows.filter((_, i) => i !== index) }));
  }

  if (loading) return <p className="Fitness-loading">Loading templates...</p>;

  // Template editor
  if (editing) {
    return (
      <div className="SwimTemplates-editor">
        <h2>{editing.id ? 'Edit Template' : 'New Template'}</h2>
        {error && <p className="Fitness-error">{error}</p>}
        <input
          className="Fitness-input"
          type="text"
          placeholder="Template name"
          value={editing.name}
          onChange={e => setEditing(prev => ({ ...prev, name: e.target.value }))}
        />
        <input
          className="Fitness-input"
          type="text"
          placeholder="Description (optional)"
          value={editing.description}
          onChange={e => setEditing(prev => ({ ...prev, description: e.target.value }))}
        />

        <div className="SwimTemplates-section-group">
          {editing.rows.map((row, i) => (
            <div key={i} className="SwimTemplates-set">
              <div className="SwimTemplates-set-fields">
                <div className="SwimTemplates-set-row">
                  <label className="SwimTemplates-notes-label">
                    <span>Exercise</span>
                    <input className="Fitness-input" type="text" value={row.exercise}
                      onChange={e => updateRow(i, 'exercise', e.target.value)} />
                  </label>
                </div>
                <div className="SwimTemplates-set-row">
                  <label>
                    <span>Sets</span>
                    <input className="Fitness-input Fitness-input--tiny" type="number" min="1" value={row.sets}
                      onChange={e => updateRow(i, 'sets', parseInt(e.target.value) || 1)} />
                  </label>
                  <span className="SwimTemplates-x">x</span>
                  <label>
                    <span>Reps</span>
                    <input className="Fitness-input Fitness-input--tiny" type="number" min="1" value={row.reps}
                      onChange={e => updateRow(i, 'reps', parseInt(e.target.value) || 1)} />
                  </label>
                  <label>
                    <span>Weight (lb)</span>
                    <input className="Fitness-input Fitness-input--tiny" type="number" min="0" step="2.5"
                      value={row.weightLb ?? ''}
                      onChange={e => updateRow(i, 'weightLb', e.target.value)} />
                  </label>
                  <label>
                    <span>Rest (sec)</span>
                    <input className="Fitness-input Fitness-input--tiny" type="number" min="0" step="15" value={row.restSeconds}
                      onChange={e => updateRow(i, 'restSeconds', e.target.value)} />
                  </label>
                </div>
                <div className="SwimTemplates-set-row">
                  <label className="SwimTemplates-notes-label">
                    <span>Muscles (comma-separated)</span>
                    <input className="Fitness-input" type="text" placeholder="quads, glutes"
                      value={musclesToText(row.muscleGroups)}
                      onChange={e => updateRow(i, 'muscleGroups', textToMuscles(e.target.value))} />
                  </label>
                </div>
                <div className="SwimTemplates-set-row">
                  <label className="SwimTemplates-notes-label">
                    <span>Notes</span>
                    <input className="Fitness-input" type="text" value={row.notes}
                      onChange={e => updateRow(i, 'notes', e.target.value)} />
                  </label>
                </div>
                {/* The dumbbell fallback the runner offers as a swap. Blank
                    exercise = no fallback; the alt inherits the primary's
                    muscles unless the JSON says otherwise. */}
                <div className="SwimTemplates-set-row">
                  <label className="SwimTemplates-notes-label">
                    <span>DB fallback</span>
                    <input className="Fitness-input" type="text" placeholder="e.g. Goblet Squat"
                      value={row.alt?.exercise ?? ''}
                      onChange={e => updateAlt(i, 'exercise', e.target.value)} />
                  </label>
                  <label>
                    <span>Reps</span>
                    <input className="Fitness-input Fitness-input--tiny" type="number" min="1"
                      value={row.alt?.reps ?? ''}
                      onChange={e => updateAlt(i, 'reps', e.target.value)} />
                  </label>
                  <label>
                    <span>Weight (lb)</span>
                    <input className="Fitness-input Fitness-input--tiny" type="number" min="0" step="2.5"
                      value={row.alt?.weightLb ?? ''}
                      onChange={e => updateAlt(i, 'weightLb', e.target.value)} />
                  </label>
                </div>
              </div>
              <div className="SwimTemplates-set-actions">
                <button className="Fitness-btn-icon Fitness-btn-icon--delete" onClick={() => removeRow(i)} title="Remove exercise">&times;</button>
              </div>
            </div>
          ))}
          <button
            className="Fitness-btn Fitness-btn--small Fitness-btn--secondary SwimTemplates-section-add-set"
            onClick={addRow}
          >+ Add Exercise</button>
        </div>

        <div className="SwimTemplates-editor-actions">
          <button className="Fitness-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Template'}
          </button>
          <button className="Fitness-btn Fitness-btn--secondary" onClick={() => setEditing(null)}>Cancel</button>
        </div>
      </div>
    );
  }

  // Template list view
  return (
    <div className="SwimTemplates">
      {error && <p className="Fitness-error">{error}</p>}

      <div className="SwimTemplates-view-toggle">
        <button
          className={`SwimTemplates-toggle-btn${viewMode === 'mine' ? ' SwimTemplates-toggle-btn--active' : ''}`}
          onClick={() => setViewMode('mine')}
        >
          My Templates ({myTemplates.length})
        </button>
        <button
          className={`SwimTemplates-toggle-btn${viewMode === 'family' ? ' SwimTemplates-toggle-btn--active' : ''}`}
          onClick={() => setViewMode('family')}
        >
          Family Templates ({familyTemplates.length})
        </button>
      </div>

      {viewMode === 'mine' && (
        <button className="Fitness-btn" onClick={startNew}>+ New Template</button>
      )}

      {displayedTemplates.length === 0 && viewMode === 'mine' && (
        <p className="Fitness-empty">No templates yet. Create one to get started.</p>
      )}
      {displayedTemplates.length === 0 && viewMode === 'family' && (
        <p className="Fitness-empty">No family templates shared yet.</p>
      )}

      <div className="SwimTemplates-list">
        {displayedTemplates.map(tmpl => {
          const isOwner = tmpl.createdByEmail.toLowerCase() === userEmail.toLowerCase();
          const creatorName = nicknames[tmpl.createdByEmail] || tmpl.createdByEmail;
          const isExpanded = expandedId === tmpl.id;
          const exerciseCount = tmpl.rows.length;
          return (
            <div key={tmpl.id} className="SwimTemplates-card">
              <div
                className="SwimTemplates-card-header"
                onClick={() => setExpandedId(isExpanded ? null : tmpl.id)}
              >
                <div className="SwimTemplates-card-header-left">
                  <h3>{tmpl.name}</h3>
                  <span className="SwimTemplates-card-meta">
                    {exerciseCount} exercise{exerciseCount !== 1 ? 's' : ''}
                    {!isOwner && <> &middot; <em>{creatorName}</em></>}
                  </span>
                </div>
                <span className={`SwimTemplates-card-chevron${isExpanded ? ' SwimTemplates-card-chevron--open' : ''}`}>&#9660;</span>
              </div>
              {isExpanded && (
                <div className="SwimTemplates-card-body">
                  {tmpl.description && <p className="SwimTemplates-card-desc">{tmpl.description}</p>}
                  <div className="SwimTemplates-card-sections">
                    <div className="SwimTemplates-card-section">
                      <div className="SwimTemplates-card-preview">
                        {tmpl.rows.map((r, i) => (
                          <span key={i} className="SwimTemplates-card-set-preview">
                            {r.exercise} {r.sets}x{r.reps}
                            {r.weightLb !== null && r.weightLb !== undefined && r.weightLb !== '' ? ` @ ${r.weightLb}` : ''}
                            {r.alt?.exercise ? ` (DB: ${r.alt.exercise})` : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="SwimTemplates-card-actions">
                    {isOwner && (
                      <>
                        <button className="Fitness-btn Fitness-btn--small" onClick={() => startEdit(tmpl)}>Edit</button>
                        <button className="Fitness-btn Fitness-btn--small Fitness-btn--danger" onClick={() => handleDelete(tmpl)}>Delete</button>
                      </>
                    )}
                    {!isOwner && (
                      <button
                        className="Fitness-btn Fitness-btn--small"
                        onClick={() => handleCopy(tmpl)}
                        disabled={copying === tmpl.id}
                      >
                        {copying === tmpl.id ? 'Copying...' : 'Copy to My Templates'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default LiftingTemplates;
